import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Building2,
  Home,
  MapPin,
  Maximize,
  Bed,
  Bath,
  Phone,
  Eye,
  ChevronRight,
  ChevronLeft,
  X,
  AlertCircle,
  Filter,
  Layers,
  Calendar,
  ArrowUpDown,
  Grid,
  List,
  Compass,
  Tag,
  FileText,
  Share2,
  ArrowUp
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { useToast } from '../context/ToastContext';
import {
  formatPrice,
  formatArea,
  getPropertyTypeColor,
  getStatusColor,
  formatNumberWithCommas,
  parseFormattedNumber,
  getPropertyTypeLabel
} from '../utils/helpers';
import { buildOfferShareText, buildOfferShareUrl, stripTrailingMiskOfferLinks } from '../utils/offerShareUtils';
import catalogService from '../services/catalogService';
import SearchableSelect from '../components/SearchableSelect';
import MultiSelect from '../components/MultiSelect';
import './OffersPage.css';
import './PublicCatalogView.css';

const DEFAULT_CATALOG_FIELDS = {
  price: true,
  area: true,
  rooms: true,
  location: true,
  images: true,
  description: true,
  contact: true,
  offerNature: true,
  status: true
};

const OFFERS_PAGE_SIZE = 24;

const HOUSE_PLACEHOLDERS = [
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='280' viewBox='0 0 400 280'%3E%3Crect width='400' height='280' rx='18' fill='%23f4f6f8'/%3E%3Cpath d='M115 170L200 105L285 170V230H235V188H165V230H115V170Z' fill='%23d7dee5'/%3E%3Cpath d='M200 86L305 166L290 184L200 116L110 184L95 166L200 86Z' fill='%2394a3b8'/%3E%3Crect x='178' y='194' width='44' height='36' rx='4' fill='%2394a3b8'/%3E%3Ccircle cx='200' cy='58' r='18' fill='%23d7dee5'/%3E%3C/svg%3E"
];

const getOfferImage = (offer) => {
  if (!offer) return null;
  if (offer.coverImage) return offer.coverImage;
  if (offer.images && offer.images.length > 0) return offer.images[0];
  // صورة بيت افتراضية محددة بشكل ثابت بناءً على id العرض
  const idx = offer.id ? offer.id.charCodeAt(offer.id.length - 1) % HOUSE_PLACEHOLDERS.length : 0;
  return HOUSE_PLACEHOLDERS[idx];
};

const getOfferMediaItems = (offer) => {
  const images = (offer?.images || []).map((src, index) => ({
    type: 'image',
    src,
    label: `صورة ${index + 1}`
  }));
  const videos = (offer?.videos || []).map((src, index) => ({
    type: 'video',
    src,
    label: `فيديو ${index + 1}`
  }));

  return [...images, ...videos];
};

// Same shape for offers everywhere, including sub-sections.
const transformCatalogOffer = (offer) => ({
  id: offer._id,
  title: offer.title,
  propertyType: offer.propertyType,
  type: offer.propertyType,
  offerType: offer.offerType,
  price: offer.price,
  area: offer.area,
  rooms: offer.rooms,
  bathrooms: offer.bathrooms,
  city: offer.city,
  district: offer.district,
  description: offer.description,
  features: offer.features || [],
  images: offer.images || [],
  videos: offer.videos || [],
  coverImage: offer.coverImage,
  age: offer.age,
  floors: offer.floors,
  streetWidth: offer.streetWidth,
  direction: offer.direction,
  deedType: offer.deedType,
  offerNature: offer.offerNature,
  acceptsPayments: offer.acceptsPayments,
  electricity: offer.electricity,
  water: offer.water,
  status: offer.status,
  externalLinks: offer.externalLinks || []
});

// Keep marketer-provided colors safe before using them in CSS variables.
const getSafeBrandColor = (color) => (
  typeof color === 'string' && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color.trim())
    ? color.trim()
    : null
);

// Light brand colors are darkened before being used behind white text.
const getReadableBrandColor = (color) => {
  const safeColor = getSafeBrandColor(color);
  if (!safeColor) return null;
  const hex = safeColor.length === 4
    ? safeColor.slice(1).split('').map((part) => part + part).join('')
    : safeColor.slice(1);
  const red = parseInt(hex.slice(0, 2), 16);
  const green = parseInt(hex.slice(2, 4), 16);
  const blue = parseInt(hex.slice(4, 6), 16);
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
  if (luminance <= 0.48) return safeColor;

  const darken = (value) => Math.max(0, Math.round(value * 0.58));
  return `#${[darken(red), darken(green), darken(blue)]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')}`;
};

const normalizeContactPhone = (phone) => {
  // Catalog extra contacts can come from settings in local or international Saudi format.
  let normalizedPhone = String(phone || '').replace(/[\s()-]/g, '');
  if (normalizedPhone.startsWith('00966')) normalizedPhone = '0' + normalizedPhone.slice(5);
  else if (normalizedPhone.startsWith('+966')) normalizedPhone = '0' + normalizedPhone.slice(4);
  else if (normalizedPhone.startsWith('966')) normalizedPhone = '0' + normalizedPhone.slice(3);
  else if (normalizedPhone.startsWith('5')) normalizedPhone = '0' + normalizedPhone;
  return normalizedPhone;
};

const getWhatsappPhone = (phone) => {
  const localPhone = normalizeContactPhone(phone);
  return localPhone.startsWith('05') ? `966${localPhone.slice(1)}` : localPhone;
};

const getTelPhone = (phone) => {
  const localPhone = normalizeContactPhone(phone);
  return localPhone.startsWith('0') ? `+966${localPhone.slice(1)}` : localPhone;
};

const CATALOG_THEME_COLORS = {
  neutral: {
    primary: '#14532D',
    page: '#F8FAFC',
    card: '#FFFFFF',
    border: '#E5E7EB',
    text: '#111827',
    muted: '#6B7280'
  },
  classic: {
    primary: '#2C1810',
    page: '#f8fafb',
    card: '#FFFFFF',
    border: '#e9ecef',
    text: '#333333',
    muted: '#666666'
  }
};

const getCatalogThemeValues = (theme, brandColor) => {
  // Logo-derived custom theme is paused; render saved custom values with the stable neutral theme.
  const normalizedTheme = ['neutral', 'classic'].includes(theme) ? theme : 'neutral';
  const palette = normalizedTheme === 'classic'
    ? CATALOG_THEME_COLORS.classic
    : CATALOG_THEME_COLORS.neutral;
  const primary = getReadableBrandColor(palette.primary) || CATALOG_THEME_COLORS.neutral.primary;

  return {
    themeName: normalizedTheme,
    primary,
    page: palette.page,
    card: palette.card,
    border: palette.border,
    text: palette.text,
    muted: palette.muted
  };
};

// Only render image values that look like real browser-loadable URLs.
const getSafeImageSrc = (image) => {
  if (!image) return null;
  if (typeof image === 'object') return image.url || image.src || null;
  if (typeof image !== 'string') return null;
  const value = image.trim();
  if (!value) return null;
  return /^(https?:|data:image\/|blob:|\/)/i.test(value) ? value : null;
};

const PublicCatalogView = () => {
  const { username, slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [fullscreenMedia, setFullscreenMedia] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const getOfferImages = (offer) => {
    if (!offer) return [];
    if (offer.images && offer.images.length > 0) return offer.images;
    if (offer.coverImage) return [offer.coverImage];
    // استخدام صورة بيت افتراضية إذا لم تكن هناك صور
    const idx = offer.id ? offer.id.charCodeAt(offer.id.length - 1) % HOUSE_PLACEHOLDERS.length : 0;
    return [HOUSE_PLACEHOLDERS[idx]];
  };

  // Filter states — initialized from URL query params on first load
  const [filters, setFilters] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      priceMin:     p.get('priceMin')     || '',
      priceMax:     p.get('priceMax')     || '',
      areaMin:      p.get('areaMin')      || '',
      areaMax:      p.get('areaMax')      || '',
      propertyType: p.getAll('propertyType') || [],
      offerType:    p.get('offerType')    || '',
      city:         p.getAll('city') || [],
      district:     p.getAll('district') || [],
      deedType:     p.get('deedType')     || '',
      offerNature:  p.get('offerNature')  || '',
      rooms:        p.get('rooms')        || '',
    };
  });
  const [pendingFilters, setPendingFilters] = useState({
    priceMin: '', priceMax: '', areaMin: '', areaMax: '',
    propertyType: [], offerType: '', city: [], district: [],
    deedType: '', offerNature: '', rooms: '',
  });
  const [descExpanded, setDescExpanded] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedQuickFilterId, setSelectedQuickFilterId] = useState(null);
  const [showSectionFilters, setShowSectionFilters] = useState(false);
  const [sectionSortBy, setSectionSortBy] = useState('newest'); // ترتيب العروض داخل القسم المفتوح
  const [showSectionSortMenu, setShowSectionSortMenu] = useState(false);
  const [mainSortBy, setMainSortBy] = useState('newest'); // ترتيب العروض في الصفحة الرئيسية
  const [showMainSortMenu, setShowMainSortMenu] = useState(false);
  const [sectionFilters, setSectionFilters] = useState({
    offerType: '', propertyType: [], city: [], district: [],
    priceMin: '', priceMax: '', areaMin: '', areaMax: '', deedType: '', offerNature: '', rooms: ''
  });
  const [publicViewMode, setPublicViewMode] = useState('list'); // 'list' أو 'gallery'
  const [openDotsMenu, setOpenDotsMenu] = useState(null); // offer id with open dots menu
  const [dotsMenuPosition, setDotsMenuPosition] = useState(null); // مكان القائمة فوق الشاشة بدون التأثير على العرض
  const [copyLinkSuccess, setCopyLinkSuccess] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [mainVisibleOffers, setMainVisibleOffers] = useState(OFFERS_PAGE_SIZE);
  const [sectionVisibleOffers, setSectionVisibleOffers] = useState(OFFERS_PAGE_SIZE);
  const [marketerAvatarFailed, setMarketerAvatarFailed] = useState(false);
  // Use marketer data first, but keep old catalog.user data working.
  const marketer = catalog?.marketer || catalog?.user;
  const marketerUsername = username || marketer?.username;
  const marketerBrandColor = getSafeBrandColor(marketer?.brandColor);
  // Avatar is the public logo/account image; heroImage remains only for the cover.
  const marketerImageSrc = !marketerAvatarFailed ? getSafeImageSrc(marketer?.avatarUrl || marketer?.avatar) : null;
  const catalogTheme = getCatalogThemeValues(marketer?.landingTheme, marketerBrandColor || catalog?.themeColor);
  const catalogThemeColor = catalogTheme.primary;
  const mainPhoneKey = normalizeContactPhone(marketer?.phone);
  // The catalog shows extra contact numbers once, not repeated on every property card.
  const catalogAdditionalContacts = Array.isArray(marketer?.additionalContactNumbers)
    ? marketer.additionalContactNumbers.filter(contact => (
      contact?.showInCatalog !== false &&
      contact?.phone &&
      normalizeContactPhone(contact.phone) !== mainPhoneKey
    ))
    : [];
  // Share links should follow the current public route style.
  const baseCatalogPath = catalog
    ? (username ? `/p/${username}/catalog/${catalog.slug}` : `/c/${catalog.slug}`)
    : '';
  const baseCatalogUrl = catalog ? `${window.location.origin}${baseCatalogPath}` : '';
  const getPreferredCatalogUrl = () => {
    if (!catalog) return window.location.href;
    const preferredUsername = username || catalog.marketer?.username || catalog.user?.username;
    const preferredPath = preferredUsername
      ? `/p/${preferredUsername}/catalog/${catalog.slug}`
      : `/c/${catalog.slug}`;
    return `${window.location.origin}${preferredPath}`;
  };

  const findOfferById = useCallback((catalogData, offerId) => {
    if (!catalogData || !offerId) return null;
    const normalizedId = String(offerId);

    const directOffer = (catalogData.offers || []).find((offer) => String(offer.id) === normalizedId);
    if (directOffer) return directOffer;

    for (const section of (catalogData.sections || [])) {
      const sectionOffer = (section.offers || []).find((offer) => String(offer.id) === normalizedId);
      if (sectionOffer) return sectionOffer;

      // Offers inside sub-sections should open from ?offer= links too.
      for (const subSection of (section.subSections || [])) {
        const subSectionOffer = (subSection.offers || []).find((offer) => String(offer.id) === normalizedId);
        if (subSectionOffer) return subSectionOffer;
      }
    }

    return null;
  }, []);

  useEffect(() => {
    // Show a small mobile shortcut after the visitor scrolls past the header area.
    const handleScroll = () => setShowScrollTop(window.scrollY > 560);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    // Reset the visible batch when the main list changes.
    setMainVisibleOffers(OFFERS_PAGE_SIZE);
  }, [filters, mainSortBy, catalog?.slug]);

  useEffect(() => {
    // If a new marketer/catalog loads, try the avatar again before falling back to the first letter.
    setMarketerAvatarFailed(false);
  }, [marketer?.avatarUrl, marketer?.avatar]);

  useEffect(() => {
    // Reset the section batch when visitors switch section or section filters.
    setSectionVisibleOffers(OFFERS_PAGE_SIZE);
  }, [selectedSection?.id, sectionFilters, sectionSortBy]);

  // Fetch catalog data from API
  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await catalogService.getPublicBySlug(encodeURIComponent(slug));

        if (response.success && response.data?.catalog) {
          const apiCatalog = response.data.catalog;

          // Transform API response to match component's expected structure
          const transformedCatalog = {
            id: apiCatalog._id,
            name: apiCatalog.name,
            description: apiCatalog.description,
            slug: apiCatalog.slug,
            isDefault: apiCatalog.isDefault || false,
            theme: apiCatalog.theme || 'default',
            // Prefer the marketer brand color, then keep the catalog's existing default.
            themeColor: getReadableBrandColor(apiCatalog.user?.brandColor) || '#1a5f4a',
            marketer: {
              name: apiCatalog.user?.name || 'غير محدد',
              username: apiCatalog.user?.username || '',
              avatar: apiCatalog.user?.avatar || '',
              avatarUrl: apiCatalog.user?.avatarUrl || '',
              heroImage: apiCatalog.user?.heroImage || '',
              brandColor: apiCatalog.user?.brandColor || '',
              landingTheme: apiCatalog.user?.landingTheme || 'neutral',
              phone: apiCatalog.user?.publicContactPhone || '',
              whatsapp: apiCatalog.user?.publicWhatsappPhone ? '966' + apiCatalog.user.publicWhatsappPhone.replace(/^0/, '') : '',
              email: apiCatalog.user?.publicContactEmail || '',
              // Extra contacts are shown once in the catalog header area, not inside every offer card.
              additionalContactNumbers: Array.isArray(apiCatalog.user?.additionalContactNumbers) ? apiCatalog.user.additionalContactNumbers : []
            },
            fields: {
              ...DEFAULT_CATALOG_FIELDS,
              ...(apiCatalog.visibleFields || {})
            },
            // Quick filters are public shortcut chips for the default catalog only.
            quickFilters: (apiCatalog.quickFilters || []).map((quickFilter, index) => ({
              id: quickFilter._id || quickFilter.id || `quick-${index}`,
              label: quickFilter.label,
              order: quickFilter.order || 0,
              filters: quickFilter.filters || {}
            })),
            offers: (apiCatalog.offers || []).map(transformCatalogOffer),
            sections: (apiCatalog.sections || []).map(section => ({
              id: section._id,
              name: section.name,
              order: section.order,
              sectionType: section.sectionType || 'filter',
              // فلاتر دائمة للقسم — محفوظة في الـ backend
              sectionFilters: section.filters || {},
              subSections: (section.subSections || []).map(sub => ({
                id: sub._id,
                name: sub.name,
                sectionType: sub.sectionType || 'filter',
                sectionFilters: sub.filters || {},
                offers: (sub.offers || []).map(transformCatalogOffer)
              })),
              offers: (section.offers || []).map(transformCatalogOffer)
            }))
          };

          setCatalog(transformedCatalog);

          // restore section from URL
          const sectionParam = new URLSearchParams(window.location.search).get('section');
          if (sectionParam && !transformedCatalog.isDefault) {
            if (sectionParam === '__all__') {
              // synthetic "all offers" section — will be resolved after first render
              setSelectedSection({ id: '__all__', name: 'جميع العروض', sectionFilters: {}, offers: [] });
            } else if (transformedCatalog.sections) {
              const found = transformedCatalog.sections.find(s => s.id === sectionParam) ||
                transformedCatalog.sections.flatMap(s => s.subSections || []).find(s => s.id === sectionParam);
              if (found) setSelectedSection(found);
            }
          }

          const quickFilterParam = new URLSearchParams(window.location.search).get('quickFilter');
          if (quickFilterParam && transformedCatalog.isDefault) {
            const foundQuickFilter = (transformedCatalog.quickFilters || []).find((quickFilter) => quickFilter.id === quickFilterParam);
            if (foundQuickFilter) setSelectedQuickFilterId(foundQuickFilter.id);
          }

          const offerFromQuery = searchParams.get('offer');
          if (offerFromQuery) {
            const targetOffer = findOfferById(transformedCatalog, offerFromQuery);
            if (targetOffer) {
              setSelectedOffer(targetOffer);
            }
          }
        } else {
          setError(response.message || 'الكتالوج غير موجود');
          setCatalog(null);
        }
      } catch (err) {
        console.error('Error fetching catalog:', err);
        setError('حدث خطأ في تحميل الكتالوج');
        setCatalog(null);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchCatalog();
    }
  }, [findOfferById, slug, searchParams]);

  const handleOpenOffer = (offer) => {
    setGalleryIndex(0);
    setSelectedOffer(offer);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('offer', String(offer.id));
    setSearchParams(nextParams, { replace: true });
  };

  // معرف ثابت للقائمة حتى تعمل حتى لو تغير اسم حقل id في بيانات العرض.
  const getOfferMenuId = (offer) => String(offer?.id || offer?._id || offer?.title || '');

  // يمنع ضغطة زر الثلاث نقاط من فتح بطاقة العرض نفسها.
  const stopOfferCardAction = (e) => {
    e.stopPropagation();
  };

  // يفتح أو يغلق قائمة الثلاث نقاط من بداية الضغطة قبل أن تلتقط البطاقة الحدث.
  const toggleOfferDotsMenu = (e, offer) => {
    e.preventDefault();
    e.stopPropagation();
    const menuId = getOfferMenuId(offer);
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 190;
    const menuHeight = 96;
    const margin = 12;
    const contactBarHeight = document.querySelector('.contact-bar')?.getBoundingClientRect().height || 0;
    const safeBottom = window.innerHeight - contactBarHeight - margin;
    const maxLeft = window.innerWidth - menuWidth - margin;
    const preferredTop = rect.bottom + 8;
    const preferredLeft = rect.right - menuWidth;

    // يحافظ على القائمة داخل حدود الشاشة بدل أن تظهر مقصوصة.
    setDotsMenuPosition({
      top: preferredTop + menuHeight > safeBottom
        ? Math.max(margin, rect.top - menuHeight - 8)
        : preferredTop,
      left: Math.min(Math.max(margin, preferredLeft), Math.max(margin, maxLeft)),
      maxWidth: Math.min(menuWidth, window.innerWidth - 24)
    });
    setOpenDotsMenu((currentId) => {
      if (currentId === menuId) {
        setDotsMenuPosition(null);
        return null;
      }
      return menuId;
    });
  };

  const handleCloseOffer = () => {
    setGalleryIndex(0);
    setSelectedOffer(null);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('offer');
    setSearchParams(nextParams, { replace: true });
  };

  // يغلق مودال العرض ويرجع لصفحة الأقسام الرئيسية
  const handleGoToSections = () => {
    setGalleryIndex(0);
    setSelectedOffer(null);
    setSelectedSection(null);
    setShowSectionFilters(false);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('offer');
    setSearchParams(nextParams, { replace: true });
  };

  const handleWhatsApp = (offer) => {
    if (!offer) {
      const message = `مرحباً، أرغب بالاستفسار عن العروض المتاحة في كتالوج: ${catalog.name}`;
      const url = `https://wa.me/${marketer.whatsapp}?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
      return;
    }
    const offerId = offer._id || offer.id;
    const offerUrl = buildOfferShareUrl({ username: marketerUsername, catalogSlug: slug, offerId, currentUrl: baseCatalogUrl });
    const text = `مرحباً، أرغب بالاستفسار عن:\n${offer.title}\n${offer.city} - ${offer.district}\nالسعر: ${formatPrice(offer.price)}`;
    const message = buildOfferShareText({ text, url: offerUrl });
    const url = `https://wa.me/${marketer.whatsapp}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleCall = () => {
    window.location.href = `tel:${marketer.phone}`;
  };

  // Share the public catalog itself, so visitors can send one clean catalog link.
  const handleShareCatalog = () => {
    const url = baseCatalogUrl;
    const text = `شاهد هذا الكتالوج العقاري: ${catalog.name || 'كتالوج عقاري'}`;

    if (navigator.share) {
      navigator.share({ title: catalog.name || 'كتالوج عقاري', text, url }).catch(() => {});
      return;
    }

    navigator.clipboard.writeText(url).then(() => {
      toast.success('تم نسخ رابط الكتالوج');
    });
  };

  const handleShareOffer = (e, offer) => {
    if (e) e.stopPropagation();
    const offerId = offer._id || offer.id;
    const url = buildOfferShareUrl({ username: marketerUsername, catalogSlug: slug, offerId, currentUrl: baseCatalogUrl });
    // Keep the offer link only in the url field to avoid duplicate links in share apps.
    const text = stripTrailingMiskOfferLinks(`شاهد هذا العرض العقاري:\n${offer.title} - ${offer.city || ''}`);
    if (navigator.share) {
      navigator.share({ title: offer.title, text, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url).then(() => {
        toast.success('تم نسخ رابط العرض');
      });
    }
  };

  const handleWhatsAppOffer = (e, offer) => {
    e.stopPropagation();
    handleWhatsApp(offer);
  };

  // Filter offers based on filter criteria
  const filterOffers = (offers) => {
    if (!offers) return [];

    return offers.filter(offer => {
      // Price filter
      if (filters.priceMin && offer.price < parseInt(filters.priceMin)) return false;
      if (filters.priceMax && offer.price > parseInt(filters.priceMax)) return false;

      // Area filter
      if (filters.areaMin && offer.area < parseInt(filters.areaMin)) return false;
      if (filters.areaMax && offer.area > parseInt(filters.areaMax)) return false;

      // Property type filter
      if (filters.propertyType?.length > 0 && !filters.propertyType.includes(offer.type)) return false;

      // Offer type filter (sale/rent)
      if (filters.offerType && offer.offerType !== filters.offerType) return false;

      // City filter
      if (filters.city?.length > 0 && !filters.city.includes(offer.city)) return false;

      // District filter
      if (filters.district?.length > 0 && !filters.district.includes(offer.district)) return false;

      // Deed type filter
      if (filters.deedType && offer.deedType !== filters.deedType) return false;

      // Offer nature filter
      if (filters.offerNature && offer.offerNature !== filters.offerNature) return false;

      // Rooms filter
      if (filters.rooms && offer.rooms !== parseInt(filters.rooms)) return false;

      return true;
    });
  };

  const filterSectionOffers = (offers) => {
    if (!offers) return [];
    const filtered = offers.filter(offer => {
      if (sectionFilters.offerType && offer.offerType !== sectionFilters.offerType) return false;
      if (sectionFilters.propertyType?.length > 0 && !sectionFilters.propertyType.includes(offer.type)) return false;
      if (sectionFilters.rooms && Number(offer.rooms || 0) < Number(sectionFilters.rooms)) return false;
      if (sectionFilters.city?.length > 0 && !sectionFilters.city.includes(offer.city)) return false;
      if (sectionFilters.district?.length > 0 && !sectionFilters.district.includes(offer.district)) return false;
      if (sectionFilters.priceMin && offer.price < parseInt(sectionFilters.priceMin)) return false;
      if (sectionFilters.priceMax && offer.price > parseInt(sectionFilters.priceMax)) return false;
      if (sectionFilters.areaMin && offer.area < parseInt(sectionFilters.areaMin)) return false;
      if (sectionFilters.areaMax && offer.area > parseInt(sectionFilters.areaMax)) return false;
      if (sectionFilters.deedType && offer.deedType !== sectionFilters.deedType) return false;
      if (sectionFilters.offerNature && offer.offerNature !== sectionFilters.offerNature) return false;
      return true;
    });

    // إضافة منطق الترتيب
    switch (sectionSortBy) {
      case 'newest':
        return filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      case 'oldest':
        return filtered.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
      case 'priceHigh':
        return filtered.sort((a, b) => (b.price || 0) - (a.price || 0));
      case 'priceLow':
        return filtered.sort((a, b) => (a.price || 0) - (b.price || 0));
      case 'areaHigh':
        return filtered.sort((a, b) => (b.area || 0) - (a.area || 0));
      case 'areaLow':
        return filtered.sort((a, b) => (a.area || 0) - (b.area || 0));
      default:
        return filtered;
    }
  };

  const hasSectionActiveFilters = () => Object.values(sectionFilters).some(v => Array.isArray(v) ? v.length > 0 : v !== '');
  const getSectionFilterCount = () => Object.values(sectionFilters).filter(v => Array.isArray(v) ? v.length > 0 : v !== '').length;
  const clearSectionFilters = () => setSectionFilters({ offerType: '', propertyType: [], city: [], district: [], priceMin: '', priceMax: '', areaMin: '', areaMax: '', deedType: '', offerNature: '', rooms: '' });

  const applySortToOffers = (offers, sortKey) => {
    const arr = [...offers];
    switch (sortKey) {
      case 'oldest': return arr.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
      case 'priceHigh': return arr.sort((a, b) => (b.price || 0) - (a.price || 0));
      case 'priceLow': return arr.sort((a, b) => (a.price || 0) - (b.price || 0));
      case 'areaHigh': return arr.sort((a, b) => (b.area || 0) - (a.area || 0));
      case 'areaLow': return arr.sort((a, b) => (a.area || 0) - (b.area || 0));
      default: return arr.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }
  };

  const applyQuickFilterToOffers = (offers) => {
    if (!selectedQuickFilterId || !catalog?.quickFilters?.length) return offers || [];

    const quickFilter = catalog.quickFilters.find((item) => item.id === selectedQuickFilterId);
    if (!quickFilter) return offers || [];

    return applySectionPermanentFilters(offers, quickFilter.filters || {});
  };

  const getFilteredOffers = () => {
    if (!catalog || !catalog.offers) return [];
    return applySortToOffers(filterOffers(applyQuickFilterToOffers(catalog.offers)), mainSortBy);
  };

  const getMainDisplayOffers = () => {
    if (!catalog) return [];

    // Default catalogs are now independent from sections: all offers + quick filters + advanced filters.
    const allOffers = catalog.isDefault
      ? [...(catalog.offers || [])]
      : [
          ...(catalog.offers || []),
          ...(catalog.sections || []).flatMap(s => s.offers || [])
        ];

    const uniqueOffers = allOffers.filter((offer, index, arr) => (
      arr.findIndex(item => String(item.id || item._id) === String(offer.id || offer._id)) === index
    ));

    return applySortToOffers(filterOffers(applyQuickFilterToOffers(uniqueOffers)), mainSortBy);
  };

  // بناء قائمة الأقسام للعرض: "جميع العروض" أولاً ثم الأقسام الحقيقية
  const getDisplaySections = () => {
    if (!catalog) return [];

    // جمع كل العروض (عامة + داخل الأقسام) وإزالة التكرار
    const allOffers = [
      ...(catalog.offers || []),
      ...(catalog.sections || []).flatMap(s => s.offers || [])
    ];
    const seen = new Set();
    const uniqueOffers = allOffers.filter(o => {
      const id = String(o.id || o._id || '');
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });

    const quickFilteredUniqueOffers = applyQuickFilterToOffers(uniqueOffers);

    const allOffersSection = {
      id: '__all__',
      name: 'جميع العروض',
      sectionFilters: {},
      offers: applySortToOffers(filterOffers(quickFilteredUniqueOffers), mainSortBy)
    };

    const realSections = catalog.isDefault ? [] : (catalog.sections || [])
      .map(section => ({
        ...section,
        // الأقسام اليدوية: عرض عروضها مباشرة (مع فلتر المستخدم فقط)
        // الأقسام التلقائية: تطبيق الفلاتر الدائمة أولاً ثم فلتر المستخدم
        offers: applySortToOffers(
          section.sectionType === 'manual'
            ? filterOffers(section.offers)
            : filterOffers(applySectionPermanentFilters(section.offers, section.sectionFilters)),
          mainSortBy
        )
      }))
      .filter(section => section.offers.length > 0);

    return [
      ...(allOffersSection.offers.length > 0 ? [allOffersSection] : []),
      ...realSections
    ];
  };

  // تطبيق فلاتر القسم الدائمة (المحفوظة من الـ backend)
  const applySectionPermanentFilters = (offers, sf) => {
    if (!sf || !Object.keys(sf).length) return offers;
    return (offers || []).filter(offer => {
      if (sf.offerType && offer.offerType !== sf.offerType) return false;
      // Section filters may be saved as arrays from the dashboard, or strings from old data.
      if (Array.isArray(sf.propertyType) && sf.propertyType.length > 0 && !sf.propertyType.includes(offer.type)) return false;
      if (!Array.isArray(sf.propertyType) && sf.propertyType && offer.type !== sf.propertyType) return false;
      if (Array.isArray(sf.city) && sf.city.length > 0 && !sf.city.includes(offer.city)) return false;
      if (!Array.isArray(sf.city) && sf.city && offer.city !== sf.city) return false;
      if (Array.isArray(sf.district) && sf.district.length > 0 && !sf.district.includes(offer.district)) return false;
      if (!Array.isArray(sf.district) && sf.district && offer.district !== sf.district) return false;
      const priceGte = Number(sf.priceMin || sf.price?.gte || 0);
      const priceLte = Number(sf.priceMax || sf.price?.lte || 0);
      const areaGte = Number(sf.areaMin || sf.area?.gte || 0);
      const areaLte = Number(sf.areaMax || sf.area?.lte || 0);
      if (priceGte > 0 && offer.price < priceGte) return false;
      if (priceLte > 0 && offer.price > priceLte) return false;
      if (areaGte > 0 && offer.area < areaGte) return false;
      if (areaLte > 0 && offer.area > areaLte) return false;
      return true;
    });
  };

  const getFilteredTotalCount = () => {
    if (catalog?.isDefault) {
      return getMainDisplayOffers().length;
    }

    return getDisplaySections().reduce((acc, s) => acc + s.offers.length, 0);
  };

  const handleFilterChange = (key, value) => {
    setPendingFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setFilters(pendingFilters);
    setShowFilters(false);
  };

  // Sync filters → URL query params whenever filters change
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const filterKeys = ['propertyType','offerType','city','district','priceMin','priceMax','areaMin','areaMax','deedType','offerNature','rooms'];
    filterKeys.forEach(key => {
      next.delete(key);
      const val = filters[key];
      if (Array.isArray(val)) {
        val.forEach(v => next.append(key, v));
      } else if (val) {
        next.set(key, val);
      }
    });
    setSearchParams(next, { replace: true });
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedSection) return;
    const next = new URLSearchParams(window.location.search);
    const filterKeys = ['propertyType','offerType','city','district','priceMin','priceMax','areaMin','areaMax','deedType','offerNature','rooms'];
    filterKeys.forEach(key => {
      if (sectionFilters[key]) {
        next.set(key, sectionFilters[key]);
      } else {
        next.delete(key);
      }
    });
    window.history.replaceState(null, '', `${window.location.pathname}?${next.toString()}`);
  }, [sectionFilters, selectedSection]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const next = new URLSearchParams(window.location.search);
    if (selectedSection) {
      next.set('section', selectedSection.id);
    } else {
      next.delete('section');
    }
    window.history.replaceState(null, '', `${window.location.pathname}?${next.toString()}`);
  }, [selectedSection]);

  useEffect(() => {
    if (selectedSection?.id === '__all__' && selectedSection.offers?.length === 0 && catalog) {
      const sections = getDisplaySections();
      const realSection = sections.find(s => s.id === '__all__');
      if (realSection && realSection.offers.length > 0) {
        setSelectedSection(realSection);
      }
    }
  }, [catalog]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!openDotsMenu) return;
    // أي ضغطة خارج القائمة تغلقها وتمنع فتح البطاقة الموجودة تحت مكان الضغطة.
    const close = (event) => {
      if (event.target.closest('.offer-card-dots-wrapper')) return;
      event.preventDefault();
      event.stopPropagation();
      setOpenDotsMenu(null);
      setDotsMenuPosition(null);
    };
    document.addEventListener('pointerdown', close, true);
    return () => document.removeEventListener('pointerdown', close, true);
  }, [openDotsMenu]);

  const addFilterParams = (params, sourceFilters = {}) => {
    const filterKeys = ['propertyType','offerType','city','district','priceMin','priceMax','areaMin','areaMax','deedType','offerNature','rooms'];
    filterKeys.forEach(key => {
      const val = sourceFilters[key];
      if (Array.isArray(val)) {
        val.filter(Boolean).forEach(v => params.append(key, v));
      } else if (val !== undefined && val !== null && val !== '') {
        params.set(key, val);
      }
    });
  };

  const buildFilteredCatalogUrl = () => {
    const params = new URLSearchParams();
    const activeQuickFilter = selectedQuickFilterId
      ? (catalog?.quickFilters || []).find((item) => item.id === selectedQuickFilterId)
      : null;

    // Quick filters are copied as their real filters so the link opens with the same results.
    if (activeQuickFilter?.filters) {
      params.set('quickFilter', activeQuickFilter.id);
      addFilterParams(params, activeQuickFilter.filters);
    }

    const mainFiltersToCopy = showFilters ? pendingFilters : filters;
    const activeFilters = selectedSection && !catalog?.isDefault ? sectionFilters : mainFiltersToCopy;
    addFilterParams(params, activeFilters);

    if (selectedSection && !catalog?.isDefault) {
      params.set('section', selectedSection.id);
    }

    const query = params.toString();
    return `${getPreferredCatalogUrl()}${query ? `?${query}` : ''}`;
  };

  const copyTextToClipboard = async (text) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // Fall back to a temporary textarea for browsers that block clipboard access.
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    let copied = false;
    try {
      copied = document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
    return copied;
  };

  const copyFilteredLink = async () => {
    const url = buildFilteredCatalogUrl();
    const copied = await copyTextToClipboard(url);
    if (!copied) {
      toast.error('تعذر نسخ الرابط');
      return;
    }
    toast.success('تم نسخ الرابط المفلتر');
    setCopyLinkSuccess(true);
    setTimeout(() => setCopyLinkSuccess(false), 2000);
  };

  const clearFilters = () => {
    const empty = {
      priceMin: '', priceMax: '', areaMin: '', areaMax: '',
      propertyType: [], offerType: '', city: [], district: [],
      deedType: '', offerNature: '', rooms: ''
    };
    setFilters(empty);
    setPendingFilters(empty);
  };

  const handleSelectQuickFilter = (quickFilterId) => {
    setSelectedQuickFilterId(quickFilterId);
    setSelectedSection(null);
    setShowSectionFilters(false);
    clearSectionFilters();
    setMainVisibleOffers(OFFERS_PAGE_SIZE);
  };

  const handleShowAllDefaultOffers = () => {
    clearFilters();
    setSelectedQuickFilterId(null);
    setSelectedSection(null);
    setShowSectionFilters(false);
    clearSectionFilters();
    setMainVisibleOffers(OFFERS_PAGE_SIZE);
  };

  const clearAllAppliedFilters = () => {
    clearFilters();
    setSelectedQuickFilterId(null);
    if (catalog?.isDefault) {
      setSelectedSection(null);
      setShowSectionFilters(false);
      clearSectionFilters();
    }
    setMainVisibleOffers(OFFERS_PAGE_SIZE);
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some(value =>
      Array.isArray(value) ? value.length > 0 : value !== ''
    );
  };

  // Get unique values for filter dropdowns / pill grids
  const getUniqueCities = () => {
    if (!catalog) return [];
    const allOffers = [
      ...(catalog.offers || []),
      ...(catalog.sections || []).flatMap(s => s.offers || [])
    ];
    return [...new Set(allOffers.map(offer => offer.city))].filter(Boolean);
  };


  const getUniqueDistricts = () => {
    if (!catalog) return [];
    const allOffers = [...(catalog.offers || []), ...(catalog.sections || []).flatMap(s => s.offers || [])];
    // If city filter is active, only show districts for selected cities
    const filtered = filters.city?.length > 0 ? allOffers.filter(o => filters.city.includes(o.city)) : allOffers;
    return [...new Set(filtered.map(o => o.district))].filter(Boolean);
  };

  const getUniqueDeedTypes = () => {
    if (!catalog) return [];
    const allOffers = [...(catalog.offers || []), ...(catalog.sections || []).flatMap(s => s.offers || [])];
    return [...new Set(allOffers.map(o => o.deedType))].filter(Boolean);
  };

  const getUniqueOfferNatures = () => {
    if (!catalog) return [];
    const allOffers = [...(catalog.offers || []), ...(catalog.sections || []).flatMap(s => s.offers || [])];
    return [...new Set(allOffers.map(o => o.offerNature))].filter(Boolean);
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(value =>
      Array.isArray(value) ? value.length > 0 : value !== ''
    ).length;
  };

  const getDeedTypeLabel = (type) => {
    switch(type) {
      case 'free': return 'حر';
      case 'mortgaged': return 'مرهون';
      default: return type;
    }
  };

  const getOfferNatureLabel = (nature) => {
    switch(nature) {
      case 'owner': return 'مع المالك';
      case 'direct': return 'مباشر';
      case 'withDirect': return 'مع المباشر';
      case 'multiple': return 'أكثر من وسيط';
      case 'indirect': return 'غير مباشر';
      default: return nature;
    }
  };

  const getOfferTypeLabel = (type) => {
    if (type === 'sale') return 'للبيع';
    if (type === 'rent') return 'للإيجار';
    return type;
  };

  const getAppliedFilterChips = () => {
    const chips = [];
    const activeQuickFilter = selectedQuickFilterId
      ? (catalog?.quickFilters || []).find((item) => item.id === selectedQuickFilterId)
      : null;

    if (activeQuickFilter) {
      chips.push({ key: 'quickFilter', label: activeQuickFilter.label, onRemove: () => setSelectedQuickFilterId(null) });
    }

    const addArrayChips = (key, label, formatter = (value) => value) => {
      (filters[key] || []).forEach((value) => {
        chips.push({
          key: `${key}-${value}`,
          label: `${label}: ${formatter(value)}`,
          onRemove: () => setFilters((prev) => ({ ...prev, [key]: (prev[key] || []).filter((item) => item !== value) }))
        });
      });
    };

    addArrayChips('propertyType', 'نوع العقار', getPropertyTypeLabel);
    if (filters.offerType) chips.push({ key: 'offerType', label: `نوع العرض: ${getOfferTypeLabel(filters.offerType)}`, onRemove: () => setFilters((prev) => ({ ...prev, offerType: '' })) });
    addArrayChips('city', 'المدينة');
    addArrayChips('district', 'الحي');
    if (filters.priceMin) chips.push({ key: 'priceMin', label: `السعر من: ${formatNumberWithCommas(filters.priceMin)}`, onRemove: () => setFilters((prev) => ({ ...prev, priceMin: '' })) });
    if (filters.priceMax) chips.push({ key: 'priceMax', label: `السعر إلى: ${formatNumberWithCommas(filters.priceMax)}`, onRemove: () => setFilters((prev) => ({ ...prev, priceMax: '' })) });
    if (filters.areaMin) chips.push({ key: 'areaMin', label: `المساحة من: ${formatNumberWithCommas(filters.areaMin)}`, onRemove: () => setFilters((prev) => ({ ...prev, areaMin: '' })) });
    if (filters.areaMax) chips.push({ key: 'areaMax', label: `المساحة إلى: ${formatNumberWithCommas(filters.areaMax)}`, onRemove: () => setFilters((prev) => ({ ...prev, areaMax: '' })) });
    if (filters.deedType) chips.push({ key: 'deedType', label: `نوع الصك: ${getDeedTypeLabel(filters.deedType)}`, onRemove: () => setFilters((prev) => ({ ...prev, deedType: '' })) });
    if (filters.offerNature) chips.push({ key: 'offerNature', label: `صفة العرض: ${getOfferNatureLabel(filters.offerNature)}`, onRemove: () => setFilters((prev) => ({ ...prev, offerNature: '' })) });
    if (filters.rooms) chips.push({ key: 'rooms', label: `الغرف: ${filters.rooms}+`, onRemove: () => setFilters((prev) => ({ ...prev, rooms: '' })) });

    return chips;
  };



  const getStatusLabel = (status) => {
    switch (status) {
      case 'available': return 'متاح';
      case 'pending': return 'قيد التفاوض';
      case 'sold': return 'مباع';
      case 'rented': return 'مؤجر';
      case 'updated': return '';
      case 'not_updated': return '';
      default: return '';
    }
  };

  const renderOffersList = (offers) => {
    if (publicViewMode === 'gallery') {
      return (
        <div className="offers-gallery-grid">
          {offers.map((offer) => (
            <div key={offer.id} className={`offer-gallery-card ${openDotsMenu === getOfferMenuId(offer) ? 'menu-open' : ''}`} onClick={() => handleOpenOffer(offer)}>
              <div className="offer-gallery-image">
                {getOfferImage(offer) ? (
                  <img src={getOfferImage(offer)} alt={offer.title} />
                ) : (
                  <div className="no-image-placeholder"><Building2 size={32} /></div>
                )}
                {catalog.fields.price && (
                  <span className="offer-gallery-price">{formatPrice(offer.price)}</span>
                )}
              </div>
              <div className="offer-gallery-info">
                <h4>{offer.title || 'بدون عنوان'}</h4>
                {catalog.fields.location && (
                  <span><MapPin size={12} /> {offer.city || '-'}</span>
                )}
                {catalog.fields.area && Number(offer.area || 0) > 0 && (
                  <span><Maximize size={12} /> {formatArea(offer.area)}</span>
                )}
                <div
                  className={`offer-card-dots-wrapper ${openDotsMenu === getOfferMenuId(offer) ? 'menu-open' : ''}`}
                  onPointerDown={stopOfferCardAction}
                  onClick={stopOfferCardAction}
                >
                  <button
                    type="button"
                    className="offer-card-dots-btn"
                    onPointerDown={(e) => toggleOfferDotsMenu(e, offer)}
                    onClick={stopOfferCardAction}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') toggleOfferDotsMenu(e, offer);
                    }}
                    title="خيارات"
                    aria-label="خيارات العرض"
                    aria-expanded={openDotsMenu === getOfferMenuId(offer)}
                  >⋮</button>
                  <div
                    className="offer-card-dots-menu"
                    style={dotsMenuPosition ? {
                      top: `${dotsMenuPosition.top}px`,
                      left: `${dotsMenuPosition.left}px`,
                      maxWidth: `${dotsMenuPosition.maxWidth}px`
                    } : undefined}
                  >
                    <button type="button" onClick={(e) => { e.stopPropagation(); handleShareOffer(null, offer); setOpenDotsMenu(null); setDotsMenuPosition(null); }}>
                      <Share2 size={14} /> مشاركة العرض
                    </button>
                    {marketer?.whatsapp && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); handleWhatsAppOffer(e, offer); setOpenDotsMenu(null); setDotsMenuPosition(null); }}>
                        <FaWhatsapp size={14} /> تواصل مع صاحب العرض
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }
    return (
    <div className="offers-list">
      {offers.map((offer) => (
        <div key={offer.id} className={`offer-list-item ${openDotsMenu === getOfferMenuId(offer) ? 'menu-open' : ''}`} onClick={() => handleOpenOffer(offer)}>
          <div className="offer-image-small">
            {getOfferImage(offer) ? (
              <img src={getOfferImage(offer)} alt={offer.title} />
            ) : (
              <div className="no-image-placeholder">لا توجد صور</div>
            )}
          </div>

          <div className="offer-info">
            <div className="offer-badges-inline">
              <span
                className="type-badge"
                style={getPropertyTypeColor(offer.propertyType || offer.type)}
              >
                {getPropertyTypeLabel(offer.propertyType || offer.type)}
              </span>
              {catalog.fields.status && offer.status && getStatusLabel(offer.status) && (
                <span className="status-badge" style={getStatusColor(offer.status)}>
                  {getStatusLabel(offer.status)}
                </span>
              )}
            </div>

            <h3>{offer.title || 'بدون عنوان'}</h3>

            <div className="offer-meta">
              {catalog.fields.location && (
                <span><MapPin size={14} /> {offer.city || '-'} - {offer.district || '-'}</span>
              )}
              {catalog.fields.area && Number(offer.area || 0) > 0 && (
                <span><Maximize size={14} /> {formatArea(offer.area)}</span>
              )}
              {catalog.fields.rooms && Number(offer.rooms || 0) > 0 && (
                <span><Bed size={14} /> {offer.rooms} غرف</span>
              )}
            </div>

            {catalog.fields.price && (
              <span className="offer-price-inline">
                {formatPrice(offer.price)}
                {offer.offerType === 'rent' ? ' / سنوياً' : ''}
              </span>
            )}
          </div>

          <div
            className={`offer-card-dots-wrapper ${openDotsMenu === getOfferMenuId(offer) ? 'menu-open' : ''}`}
            onPointerDown={stopOfferCardAction}
            onClick={stopOfferCardAction}
          >
            <button
              type="button"
              className="offer-card-dots-btn"
              onPointerDown={(e) => toggleOfferDotsMenu(e, offer)}
              onClick={stopOfferCardAction}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') toggleOfferDotsMenu(e, offer);
              }}
              title="خيارات"
              aria-label="خيارات العرض"
              aria-expanded={openDotsMenu === getOfferMenuId(offer)}
            >⋮</button>
            <div
              className="offer-card-dots-menu"
              style={dotsMenuPosition ? {
                top: `${dotsMenuPosition.top}px`,
                left: `${dotsMenuPosition.left}px`,
                maxWidth: `${dotsMenuPosition.maxWidth}px`
              } : undefined}
            >
              <button type="button" onClick={(e) => { e.stopPropagation(); handleShareOffer(null, offer); setOpenDotsMenu(null); setDotsMenuPosition(null); }}>
                <Share2 size={14} /> مشاركة العرض
              </button>
              {marketer?.whatsapp && (
                <button type="button" onClick={(e) => { e.stopPropagation(); handleWhatsAppOffer(e, offer); setOpenDotsMenu(null); setDotsMenuPosition(null); }}>
                  <FaWhatsapp size={14} /> تواصل مع صاحب العرض
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
    );
  };

  const renderLoadMoreButton = (visibleCount, totalCount, onLoadMore) => {
    if (visibleCount >= totalCount) return null;

    return (
      <div className="catalog-load-more-wrap">
        <button type="button" className="catalog-load-more-btn" onClick={onLoadMore}>
          عرض المزيد
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="catalog-loading">
        <div className="loading-spinner"></div>
        <p>جاري تحميل الكتالوج...</p>
      </div>
    );
  }

  if (!catalog) {
    return (
      <div className="catalog-not-found">
        <div className="not-found-content">
          <AlertCircle size={64} />
          <h1>404</h1>
          <h2>{error || 'الكتالوج غير موجود'}</h2>
          <p>عذراً، لم نتمكن من العثور على الكتالوج المطلوب</p>
          <a href="/" className="btn btn-primary">العودة للرئيسية</a>
        </div>
      </div>
    );
  }

  const displaySections = getDisplaySections();
  // نعرض مجموعات مختارة فقط عندما توجد أقسام حقيقية داخل الكتالوج.
  const curatedSections = displaySections.filter(section => section.id !== '__all__');
  const activeQuickFilters = catalog?.isDefault ? (catalog.quickFilters || []) : [];
  const hasQuickFilters = activeQuickFilters.length > 0;
  const totalFilteredCount = getFilteredTotalCount();
  const mainDisplayOffers = getMainDisplayOffers();
  const visibleMainOffers = mainDisplayOffers.slice(0, mainVisibleOffers);
  const sectionDisplayOffers = selectedSection ? filterSectionOffers(selectedSection.offers) : [];
  const visibleSectionOffers = sectionDisplayOffers.slice(0, sectionVisibleOffers);
  const headerOfferCount = selectedSection ? sectionDisplayOffers.length : mainDisplayOffers.length;
  const appliedFilterChips = getAppliedFilterChips();

  return (
    <div
      className={`public-catalog catalog-theme-${catalogTheme.themeName}`}
      style={{
        '--theme-color': catalogThemeColor,
        '--primary-color': catalogThemeColor,
        '--background-color': catalogTheme.page,
        '--card-bg': catalogTheme.card,
        '--surface-color': catalogTheme.card,
        '--border-color': catalogTheme.border,
        '--border-light': catalogTheme.border,
        '--text-primary': catalogTheme.text,
        '--text-secondary': catalogTheme.muted,
        '--text-muted': catalogTheme.muted
      }}
    >
      {/* Header */}
      <header className="catalog-header">
        <div className="header-content">
          {/* Return visitors to the marketer page from route username first, then API username. */}
          {marketerUsername && (
            <Link to={`/p/${marketerUsername}`} className="catalog-back-to-marketer">
              <ChevronRight size={16} />
              العودة إلى الصفحة الرئيسية
            </Link>
          )}
          <h1>{catalog.name}</h1>

          {/* owner / meta placed with title column */}
          {marketer?.name && (
            <div className="catalog-owner" aria-hidden="false">
              {marketerImageSrc ? (
                <img
                  src={marketerImageSrc}
                  alt={marketer.name}
                  className="catalog-owner-avatar"
                  onError={() => setMarketerAvatarFailed(true)}
                />
              ) : (
                <span className="catalog-owner-initial">{marketer.name?.charAt(0)}</span>
              )}
              <span className="owner-name">بواسطة {marketer.siteName || marketer.businessName || marketer.name}</span>
            </div>
          )}

          {catalog.description && (
  <>
    <p className={`catalog-description ${descExpanded ? 'expanded' : ''}`} aria-expanded={descExpanded}>{catalog.description}</p>
    {catalog.description.length > 220 && (
      <button
        className="desc-toggle"
        onClick={(e) => { e.stopPropagation(); setDescExpanded(s => !s); }}
        aria-expanded={descExpanded}
      >
        {descExpanded ? 'عرض أقل' : 'اقرأ المزيد'}
      </button>
    )}
  </>
)}

          <div className="header-stats">
            <div className="catalog-summary-pill">{headerOfferCount} عقار متاح</div>
            <div className="controls-group" role="group" aria-label="controls">
              <button
                className={`notion-filter-btn ${showFilters ? 'active' : ''} ${hasActiveFilters() ? 'has-filters' : ''}`}
                onClick={() => { if (!showFilters) setPendingFilters(filters); setShowFilters(!showFilters); }}
                aria-expanded={showFilters}
              >
                <Filter size={18} />
                {getActiveFilterCount() > 0 && <span className="filter-count-badge">{getActiveFilterCount()}</span>}
              </button>
              {hasActiveFilters() && (
                <button className="clear-all-inline" onClick={clearFilters}>✕</button>
              )}
              <button
                className="notion-filter-btn"
                onClick={() => setPublicViewMode(v => v === 'list' ? 'gallery' : 'list')}
                title={publicViewMode === 'list' ? 'عرض معرض الصور' : 'عرض القائمة'}
              >
                {publicViewMode === 'list' ? <Grid size={18} /> : <List size={18} />}
              </button>
              <div style={{ position: 'relative' }}>
                <button
                  className="notion-filter-btn"
                  onClick={() => setShowMainSortMenu((v) => !v)}
                  title="ترتيب العروض"
                >
                  <ArrowUpDown size={18} />
                </button>
                {showMainSortMenu && (
                  <div style={{ position: 'absolute', top: '110%', right: 0, background: 'var(--surface-color, #fff)', border: '1px solid var(--border-color, #e0e0e0)', borderRadius: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 200, minWidth: '150px', overflow: 'hidden' }}>
                    {[
                      { value: 'newest', label: 'الأحدث أولاً' },
                      { value: 'oldest', label: 'الأقدم أولاً' },
                      { value: 'priceHigh', label: 'الأعلى سعراً' },
                      { value: 'priceLow', label: 'الأقل سعراً' },
                      { value: 'areaHigh', label: 'الأكبر مساحة' },
                      { value: 'areaLow', label: 'الأصغر مساحة' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        style={{ display: 'block', width: '100%', padding: '10px 16px', textAlign: 'right', background: mainSortBy === opt.value ? 'var(--primary-light, #e8f4ff)' : 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', color: 'var(--text-primary, #333)' }}
                        onClick={() => { setMainSortBy(opt.value); setShowMainSortMenu(false); }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {catalog.fields.contact && catalogAdditionalContacts.length > 0 && (
        <section className="catalog-extra-contacts" aria-label="طرق التواصل">
          <div className="catalog-extra-contacts-inner">
            <h2>طرق التواصل</h2>
            <div className="catalog-extra-contact-list">
              {catalogAdditionalContacts.map((contact, index) => (
                <div className="catalog-extra-contact-card" key={`${contact.phone}-${index}`}>
                  <div>
                    <strong>{contact.label || 'رقم تواصل'}</strong>
                    <span>{normalizeContactPhone(contact.phone)}</span>
                  </div>
                  <div className="catalog-extra-contact-actions">
                    {contact.whatsappEnabled !== false && (
                      <a href={`https://wa.me/${getWhatsappPhone(contact.phone)}`} target="_blank" rel="noreferrer">
                        <FaWhatsapp size={16} />
                        واتساب
                      </a>
                    )}
                    {contact.callEnabled === true && (
                      <a href={`tel:${getTelPhone(contact.phone)}`}>
                        <Phone size={16} />
                        اتصال
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Notion-style Filters Panel */}
      {showFilters && (
        <div className="notion-sheet-backdrop" onClick={() => setShowFilters(false)}>
          <div className="notion-filters-panel" onClick={(e) => e.stopPropagation()}>
            <div className="notion-filters-header">
              <span>الفلاتر</span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button className="notion-apply-btn" onClick={applyFilters}>تطبيق</button>
                <button className="notion-copy-link-btn" onClick={copyFilteredLink}>
                  {copyLinkSuccess ? '✅ تم النسخ' : '🔗 نسخ الرابط'}
                </button>
                {hasActiveFilters() && (
                  <button className="notion-clear-btn" onClick={clearFilters}>مسح الكل</button>
                )}
              </div>
            </div>
            <div className="notion-filters-body">
            {/* نوع العرض */}
            <div className="notion-filter-row">
              <label>نوع العرض</label>
              <select value={pendingFilters.offerType} onChange={(e) => handleFilterChange('offerType', e.target.value)}>
                <option value="">الكل</option>
                <option value="sale">للبيع</option>
                <option value="rent">للإيجار</option>
              </select>
            </div>
            {/* نوع العقار */}
            <div className="notion-filter-row">
              <label>نوع العقار</label>
              <MultiSelect
                options={[...(new Set([...(catalog.offers || []), ...(catalog.sections || []).flatMap(s => s.offers || [])].map(o => o.type).filter(Boolean)))].map(type => ({ value: type, label: getPropertyTypeLabel(type) }))}
                value={pendingFilters.propertyType}
                onChange={(val) => handleFilterChange('propertyType', val)}
                placeholder="الكل"
                searchPlaceholder="ابحث عن نوع..."
              />
            </div>
            {/* المدينة */}
            {getUniqueCities().length > 0 && (
              <div className="notion-filter-row">
                <label>المدينة</label>
                <MultiSelect
                  options={getUniqueCities().map(city => ({ value: city, label: city }))}
                  value={pendingFilters.city}
                  onChange={(val) => { handleFilterChange('city', val); handleFilterChange('district', []); }}
                  placeholder="الكل"
                  searchPlaceholder="ابحث عن مدينة..."
                />
              </div>
            )}
            {/* الحي */}
            {getUniqueDistricts().length > 0 && (
              <div className="notion-filter-row">
                <label>الحي</label>
                <MultiSelect
                  options={getUniqueDistricts().map(d => ({ value: d, label: d }))}
                  value={pendingFilters.district}
                  onChange={(val) => handleFilterChange('district', val)}
                  placeholder="الكل"
                  searchPlaceholder="ابحث عن حي..."
                />
              </div>
            )}
            {/* السعر */}
            <div className="notion-filter-row">
              <label>السعر</label>
              <div className="notion-range-inputs">
                <input type="text" placeholder="من" value={formatNumberWithCommas(pendingFilters.priceMin)} onChange={(e) => handleFilterChange('priceMin', parseFormattedNumber(e.target.value))} />
                <span>-</span>
                <input type="text" placeholder="إلى" value={formatNumberWithCommas(pendingFilters.priceMax)} onChange={(e) => handleFilterChange('priceMax', parseFormattedNumber(e.target.value))} />
              </div>
            </div>
            {/* المساحة */}
            <div className="notion-filter-row">
              <label>المساحة (م²)</label>
              <div className="notion-range-inputs">
                <input type="text" placeholder="من" value={formatNumberWithCommas(pendingFilters.areaMin)} onChange={(e) => handleFilterChange('areaMin', parseFormattedNumber(e.target.value))} />
                <span>-</span>
                <input type="text" placeholder="إلى" value={formatNumberWithCommas(pendingFilters.areaMax)} onChange={(e) => handleFilterChange('areaMax', parseFormattedNumber(e.target.value))} />
              </div>
            </div>
            {/* الغرف */}
            <div className="notion-filter-row">
              <label>عدد الغرف</label>
              <select value={pendingFilters.rooms} onChange={(e) => handleFilterChange('rooms', e.target.value)}>
                <option value="">الكل</option>
                {[...(new Set([...(catalog.offers || []), ...(catalog.sections || []).flatMap(s => s.offers || [])].map(o => Number(o.rooms || 0)).filter(n => n > 0)))].sort((a, b) => a - b).map(n => (
                  <option key={n} value={String(n)}>{n}+</option>
                ))}
              </select>
            </div>
            {/* نوع الصك */}
            {getUniqueDeedTypes().length > 0 && (
              <div className="notion-filter-row">
                <label>نوع الصك</label>
                <select value={pendingFilters.deedType} onChange={(e) => handleFilterChange('deedType', e.target.value)}>
                  <option value="">الكل</option>
                  {getUniqueDeedTypes().map(dt => (
                    <option key={dt} value={dt}>{getDeedTypeLabel(dt)}</option>
                  ))}
                </select>
              </div>
            )}
            {/* صفة العرض */}
            {getUniqueOfferNatures().length > 0 && (
              <div className="notion-filter-row">
                <label>صفة العرض</label>
                <select value={pendingFilters.offerNature} onChange={(e) => handleFilterChange('offerNature', e.target.value)}>
                  <option value="">الكل</option>
                  {getUniqueOfferNatures().map(on => (
                    <option key={on} value={on}>{getOfferNatureLabel(on)}</option>
                  ))}
                </select>
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {appliedFilterChips.length > 0 && (
        <section className="applied-filters-bar" aria-label="الفلاتر المطبقة">
          <div className="applied-filters-header">
            <h2>الفلاتر المطبقة</h2>
            <button type="button" className="applied-filters-clear" onClick={clearAllAppliedFilters}>
              مسح الكل
            </button>
          </div>
          <div className="applied-filters-list">
            {appliedFilterChips.map((chip) => (
              <span key={chip.key} className="applied-filter-chip">
                {chip.label}
                <button type="button" onClick={chip.onRemove} aria-label={`إزالة ${chip.label}`}>
                  ×
                </button>
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Offers Grid */}
      <main className="catalog-main">
        {totalFilteredCount === 0 ? (
          <div className="empty-catalog">
            <Home size={64} />
            <h3>{hasActiveFilters() ? 'لا توجد عروض مطابقة للفلاتر المحددة' : 'لا توجد عروض في هذا الكتالوج حالياً'}</h3>
            <p>{hasActiveFilters() ? 'جرب تغيير الفلاتر للحصول على نتائج أخرى' : 'سيتم إضافة العروض قريباً'}</p>
            {hasActiveFilters() && (
              <button className="btn btn-primary" onClick={clearFilters}>
                مسح الفلاتر
              </button>
            )}
          </div>
        ) : (!catalog?.isDefault ? (
          <div className="sections-grid-wrapper">
            <div className="sections-grid">
              {displaySections.map(section => {
                const sectionOffersWithImages = section.offers.filter(o => {
                  const imgs = getOfferImages(o);
                  return imgs && imgs.length > 0;
                });
                const previewImages = [];
                sectionOffersWithImages.slice(0, 4).forEach(o => {
                  const imgs = getOfferImages(o);
                  if (imgs[0]) previewImages.push(imgs[0]);
                });
                return (
                  <div
                    key={section.id}
                    className="section-card"
                    onClick={() => setSelectedSection(section)}
                  >
                    <div className="section-card-images">
                      {previewImages.length > 0 ? (
                        previewImages.length === 1 ? (
                          <img src={previewImages[0]} alt={section.name} className="section-single-img" />
                        ) : (
                          <div className={`section-images-grid grid-${Math.min(previewImages.length, 4)}`}>
                            {previewImages.slice(0, 4).map((img, idx) => (
                              <img key={idx} src={img} alt="" />
                            ))}
                          </div>
                        )
                      ) : (
                        <div className="section-no-image">
                          <Layers size={48} />
                        </div>
                      )}
                      <div className="section-card-overlay">
                        <span className="section-offers-count">{section.offers.length} عرض</span>
                      </div>
                    </div>
                    <div className="section-card-content">
                      <h3>{section.name}</h3>
                      {section.subSections && section.subSections.length > 0 && (
                        <div className="section-card-subsections">
                          {section.subSections.map((sub, si) => (
                            <span key={sub.id || si} className="subsection-pill" onClick={(e) => { e.stopPropagation(); setSelectedSection({ ...sub, isSubSection: true, parentName: section.name }); }}>
                              📂 {sub.name}
                            </span>
                          ))}
                        </div>
                      )}
                      <span className="view-section-btn">
                        عرض العروض
                        <ChevronLeft size={16} />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          // Default catalog — chips + sections + all offers
          <>
            {hasQuickFilters && (
              <section className="catalog-quick-filters" aria-label="تصفح سريع">
                <h2 className="catalog-quick-filters-title">تصفح سريع</h2>
                <div className="catalog-quick-filters-scroll">
                  <button
                    type="button"
                    className={`catalog-quick-filter-chip ${!selectedQuickFilterId ? 'active' : ''}`}
                    onClick={handleShowAllDefaultOffers}
                  >
                    كل العروض
                  </button>
                  {activeQuickFilters.map((quickFilter) => (
                    <button
                      type="button"
                      key={quickFilter.id}
                      className={`catalog-quick-filter-chip ${selectedQuickFilterId === quickFilter.id ? 'active' : ''}`}
                      onClick={() => handleSelectQuickFilter(quickFilter.id)}
                    >
                      {quickFilter.label}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* أزرار الأقسام تستبدل اختصارات المدن والأنواع بدون تغيير منطق الفلاتر نفسه. */}
            {!catalog.isDefault && !hasQuickFilters && curatedSections.length > 0 && (
              <section className="catalog-curated-groups" aria-label="مجموعات مختارة">
                <h2 className="catalog-curated-title">مجموعات مختارة</h2>
                <div className="catalog-curated-scroll">
                  <button
                    type="button"
                    className={`catalog-curated-chip ${!selectedSection ? 'active' : ''}`}
                    onClick={() => { setSelectedSection(null); setShowSectionFilters(false); clearSectionFilters(); }}
                  >
                    كل العروض
                  </button>
                  {curatedSections.map(section => (
                    <button
                      type="button"
                      key={section.id}
                      className={`catalog-curated-chip ${selectedSection?.id === section.id ? 'active' : ''}`}
                      onClick={() => setSelectedSection(section)}
                    >
                      {section.name}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Sections grid — only if sections exist */}
            {!catalog.isDefault && !hasQuickFilters && (catalog.sections || []).length > 0 && (
              <div className="sections-grid-wrapper">
                <div className="sections-grid">
                  {getDisplaySections().filter(s => s.id !== '__all__').map(section => {
                    const sectionOffersWithImages = section.offers.filter(o => {
                      const imgs = getOfferImages(o);
                      return imgs && imgs.length > 0;
                    });
                    const previewImages = [];
                    sectionOffersWithImages.slice(0, 4).forEach(o => {
                      const imgs = getOfferImages(o);
                      if (imgs[0]) previewImages.push(imgs[0]);
                    });
                    return (
                      <div key={section.id} className="section-card" onClick={() => setSelectedSection(section)}>
                        <div className="section-card-images">
                          {previewImages.length > 0 ? (
                            previewImages.length === 1 ? (
                              <img src={previewImages[0]} alt={section.name} className="section-single-img" />
                            ) : (
                              <div className={`section-images-grid grid-${Math.min(previewImages.length, 4)}`}>
                                {previewImages.slice(0, 4).map((img, idx) => (
                                  <img key={idx} src={img} alt="" />
                                ))}
                              </div>
                            )
                          ) : (
                            <div className="section-no-image">
                              <Layers size={48} />
                            </div>
                          )}
                          <div className="section-card-overlay">
                            <span className="section-offers-count">{section.offers.length} عرض</span>
                          </div>
                        </div>
                        <div className="section-card-content">
                          <h3>{section.name}</h3>
                          {section.subSections && section.subSections.length > 0 && (
                            <div className="section-card-subsections">
                              {section.subSections.map((sub, si) => (
                                <span key={sub.id || si} className="subsection-pill" onClick={(e) => { e.stopPropagation(); setSelectedSection({ ...sub, isSubSection: true, parentName: section.name }); }}>
                                  📂 {sub.name}
                                </span>
                              ))}
                            </div>
                          )}
                          <span className="view-section-btn">
                            عرض العروض
                            <ChevronLeft size={16} />
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* All offers list */}
            <div className="catalog-main">
              {renderOffersList(visibleMainOffers)}
              {renderLoadMoreButton(
                mainVisibleOffers,
                mainDisplayOffers.length,
                () => setMainVisibleOffers(count => count + OFFERS_PAGE_SIZE)
              )}
            </div>
          </>
        ))}
      </main>

      {/* Section Full Page View */}
      {selectedSection && (
        <div className="section-fullpage">
          <div className="section-fullpage-header">
            {!catalog?.isDefault && (
              <button className="section-back-btn" onClick={() => { setSelectedSection(null); setShowSectionFilters(false); clearSectionFilters(); }}>
                <ChevronRight size={20} />
                رجوع
              </button>
            )}
            <div className="section-fullpage-title">
              <span className="section-modal-icon">📁</span>
              <h2>{selectedSection.name}</h2>
              <span className="section-modal-count">{filterSectionOffers(selectedSection.offers).length} عرض</span>
            </div>
          </div>

          {/* Section Filter Bar */}
          <div className="section-filter-bar">
            <div className="section-filter-left">
              <button
                className={`notion-filter-btn small ${showSectionFilters ? 'active' : ''} ${hasSectionActiveFilters() ? 'has-filters' : ''}`}
                onClick={() => setShowSectionFilters(!showSectionFilters)}
              >
                <Filter size={16} />
                {getSectionFilterCount() > 0 && <span className="filter-count-badge">{getSectionFilterCount()}</span>}
              </button>
              {hasSectionActiveFilters() && (
                <button className="clear-all-inline" onClick={clearSectionFilters}>✕</button>
              )}

              <div style={{ position: 'relative' }}>
                <button
                  className="notion-filter-btn small"
                  onClick={() => setShowSectionSortMenu((v) => !v)}
                  title="ترتيب العروض"
                >
                  <ArrowUpDown size={16} />
                </button>
                {showSectionSortMenu && (
                  <div style={{ position: 'absolute', top: '110%', right: 0, background: 'var(--surface-color, #fff)', border: '1px solid var(--border-color, #e0e0e0)', borderRadius: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 200, minWidth: '150px', overflow: 'hidden' }}>
                    {[
                      { value: 'newest', label: 'الأحدث أولاً' },
                      { value: 'oldest', label: 'الأقدم أولاً' },
                      { value: 'priceHigh', label: 'الأعلى سعراً' },
                      { value: 'priceLow', label: 'الأقل سعراً' },
                      { value: 'areaHigh', label: 'الأكبر مساحة' },
                      { value: 'areaLow', label: 'الأصغر مساحة' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        style={{ display: 'block', width: '100%', padding: '10px 16px', textAlign: 'right', background: sectionSortBy === opt.value ? 'var(--primary-light, #e8f4ff)' : 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', color: 'var(--text-primary, #333)' }}
                        onClick={() => { setSectionSortBy(opt.value); setShowSectionSortMenu(false); }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                className="notion-filter-btn small"
                onClick={() => setPublicViewMode(v => v === 'list' ? 'gallery' : 'list')}
                title={publicViewMode === 'list' ? 'عرض معرض الصور' : 'عرض القائمة'}
              >
                {publicViewMode === 'list' ? <Grid size={16} /> : <List size={16} />}
              </button>
              <span className="section-offers-total">{filterSectionOffers(selectedSection.offers).length} عرض</span>
            </div>
          </div>

          {/* Section Filters Panel */}
          {showSectionFilters && (
            <div className="notion-sheet-backdrop" onClick={() => setShowSectionFilters(false)}>
              <div className="notion-filters-panel" onClick={(e) => e.stopPropagation()}>
                <div className="notion-filters-header">
                  <span>الفلاتر</span>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button className="notion-apply-btn" onClick={() => setShowSectionFilters(false)}>تطبيق</button>
                    <button className="notion-copy-link-btn" onClick={copyFilteredLink}>
                      {copyLinkSuccess ? '✅ تم النسخ' : '🔗 نسخ الرابط'}
                    </button>
                    {hasSectionActiveFilters() && (
                      <button className="notion-clear-btn" onClick={clearSectionFilters}>مسح الكل</button>
                    )}
                  </div>
                </div>
                <div className="notion-filters-body">
                <div className="notion-filter-row">
                  <label>نوع العرض</label>
                  <select value={sectionFilters.offerType} onChange={(e) => setSectionFilters(prev => ({ ...prev, offerType: e.target.value }))}>
                    <option value="">الكل</option>
                    <option value="sale">للبيع</option>
                    <option value="rent">للإيجار</option>
                  </select>
                </div>
                <div className="notion-filter-row">
                  <label>نوع العقار</label>
                  <MultiSelect
                    options={[...new Set((selectedSection?.offers || []).map(o => o.type).filter(Boolean))].map(type => ({ value: type, label: getPropertyTypeLabel(type) }))}
                    value={sectionFilters.propertyType}
                    onChange={(val) => setSectionFilters(prev => ({ ...prev, propertyType: val }))}
                    placeholder="الكل"
                    searchPlaceholder="ابحث عن نوع..."
                  />
                </div>
                <div className="notion-filter-row">
                  <label>المدينة</label>
                  <MultiSelect
                    options={[...new Set((selectedSection?.offers || []).map(o => o.city).filter(Boolean))].map(city => ({ value: city, label: city }))}
                    value={sectionFilters.city}
                    onChange={(val) => setSectionFilters(prev => ({ ...prev, city: val, district: [] }))}
                    placeholder="الكل"
                    searchPlaceholder="ابحث عن مدينة..."
                  />
                </div>
                <div className="notion-filter-row">
                  <label>الحي</label>
                  <MultiSelect
                    options={[...new Set((selectedSection?.offers || []).map(o => o.district).filter(Boolean))].map(d => ({ value: d, label: d }))}
                    value={sectionFilters.district}
                    onChange={(val) => setSectionFilters(prev => ({ ...prev, district: val }))}
                    placeholder="الكل"
                    searchPlaceholder="ابحث عن حي..."
                  />
                </div>
                <div className="notion-filter-row">
                  <label>السعر</label>
                  <div className="notion-range-inputs">
                    <input type="text" placeholder="من" value={formatNumberWithCommas(sectionFilters.priceMin)} onChange={(e) => setSectionFilters(prev => ({ ...prev, priceMin: parseFormattedNumber(e.target.value) }))} />
                    <span>-</span>
                    <input type="text" placeholder="إلى" value={formatNumberWithCommas(sectionFilters.priceMax)} onChange={(e) => setSectionFilters(prev => ({ ...prev, priceMax: parseFormattedNumber(e.target.value) }))} />
                  </div>
                </div>
                <div className="notion-filter-row">
                  <label>المساحة (م²)</label>
                  <div className="notion-range-inputs">
                    <input type="text" placeholder="من" value={formatNumberWithCommas(sectionFilters.areaMin)} onChange={(e) => setSectionFilters(prev => ({ ...prev, areaMin: parseFormattedNumber(e.target.value) }))} />
                    <span>-</span>
                    <input type="text" placeholder="إلى" value={formatNumberWithCommas(sectionFilters.areaMax)} onChange={(e) => setSectionFilters(prev => ({ ...prev, areaMax: parseFormattedNumber(e.target.value) }))} />
                  </div>
                </div>
                <div className="notion-filter-row">
                  <label>عدد الغرف</label>
                  <select value={sectionFilters.rooms} onChange={(e) => setSectionFilters(prev => ({ ...prev, rooms: e.target.value }))}>
                    <option value="">الكل</option>
                    {[...new Set(selectedSection.offers.map(o => Number(o.rooms || 0)).filter(n => n > 0))].sort((a, b) => a - b).map(n => (
                      <option key={n} value={String(n)}>{n}+</option>
                    ))}
                  </select>
                </div>
                {(() => { const deedTypes = [...new Set(selectedSection.offers.filter(o => o.deedType).map(o => o.deedType))]; return deedTypes.length > 0 ? (
                  <div className="notion-filter-row">
                    <label>نوع الصك</label>
                    <select value={sectionFilters.deedType} onChange={(e) => setSectionFilters(prev => ({ ...prev, deedType: e.target.value }))}>
                      <option value="">الكل</option>
                      {deedTypes.map(dt => <option key={dt} value={dt}>{getDeedTypeLabel(dt)}</option>)}
                    </select>
                  </div>
                ) : null; })()}
                {(() => { const natures = [...new Set(selectedSection.offers.filter(o => o.offerNature).map(o => o.offerNature))]; return natures.length > 0 ? (
                  <div className="notion-filter-row">
                    <label>صفة العرض</label>
                    <select value={sectionFilters.offerNature} onChange={(e) => setSectionFilters(prev => ({ ...prev, offerNature: e.target.value }))}>
                      <option value="">الكل</option>
                      {natures.map(on => <option key={on} value={on}>{getOfferNatureLabel(on)}</option>)}
                    </select>
                  </div>
                ) : null; })()}
                </div>
              </div>
            </div>
          )}

          <div className="section-fullpage-body">
            {renderOffersList(visibleSectionOffers)}
            {renderLoadMoreButton(
              sectionVisibleOffers,
              sectionDisplayOffers.length,
              () => setSectionVisibleOffers(count => count + OFFERS_PAGE_SIZE)
            )}
          </div>
        </div>
      )}

      {/* Contact Bar */}
      {(catalog.fields.contact || catalog.slug) && (
        <div className="contact-bar">
          {/* Public visitors need a catalog-level share button, not only offer sharing. */}
          <button className="contact-btn share" onClick={handleShareCatalog}>
            <Share2 size={20} />
            مشاركة الكتالوج
          </button>
          {catalog.fields.contact && (marketer?.whatsapp || marketer?.phone) && (
            <>
              {/* زر التواصل العام يرسل استفسار عن الكتالوج فقط، وليس عن أول عرض بالخطأ */}
              {marketer?.whatsapp && (
              <button className="contact-btn whatsapp" onClick={() => handleWhatsApp(null)}>
                <FaWhatsapp size={20} />
                واتساب
              </button>
              )}
              {marketer?.phone && (
              <button className="contact-btn call" onClick={handleCall}>
                <Phone size={20} />
                اتصال
              </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Offer Details Modal */}
      {selectedOffer && (
        <div className="offer-modal" onClick={handleCloseOffer}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Eye size={24} />
                تفاصيل العرض
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {/* نص الزر يوضح أنه يرجع المستخدم للصفحة الرئيسية للكتالوج */}
                <button className="btn-home-catalog" onClick={handleGoToSections} title="العودة للصفحة الرئيسية">
                  <Home size={18} />
                  <span>العودة للصفحة الرئيسية</span>
                </button>
                <button className="modal-close" onClick={handleCloseOffer}>
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="modal-body">
              <div className="view-offer-content">
                <div className="view-offer-image">
                  {(() => {
                    const mediaItems = getOfferMediaItems(selectedOffer);
                    const allImages = getOfferImages(selectedOffer);
                    const total = mediaItems.length || allImages.length;
                    if (total === 0) {
                      return (
                        <div className="no-image-placeholder" style={{ width: '100%', height: '300px', borderRadius: '12px', fontSize: '16px' }}>
                          لا توجد صور
                        </div>
                      );
                    }
                    const safeIndex = Math.min(galleryIndex, total - 1);
                    const activeMedia = mediaItems[safeIndex] || { type: 'image', src: allImages[safeIndex], label: `صورة ${safeIndex + 1}` };
                    let touchStartX = null;
                    const handleTouchStart = (e) => { touchStartX = e.touches[0].clientX; };
                    const handleTouchEnd = (e) => {
                      if (touchStartX === null) return;
                      const diff = touchStartX - e.changedTouches[0].clientX;
                      if (Math.abs(diff) > 40) {
                        // RTL: swipe left = next, swipe right = prev
                        if (diff > 0) setGalleryIndex(i => Math.min(i + 1, total - 1));
                        else setGalleryIndex(i => Math.max(i - 1, 0));
                      }
                      touchStartX = null;
                    };
                    return (
                      <div className="offer-gallery"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                      >
                        {activeMedia.type === 'video' ? (
                          <video src={activeMedia.src} controls className="gallery-main-img gallery-main-video" onDoubleClick={() => setFullscreenMedia({ items: mediaItems, index: safeIndex })} />
                        ) : (
                          <img
                            src={activeMedia.src}
                            alt={`${selectedOffer.title} - ${safeIndex + 1}`}
                            className="gallery-main-img"
                            onClick={() => setFullscreenMedia({ items: mediaItems, index: safeIndex })}
                          />
                        )}
                        <button type="button" className="gallery-fullscreen-btn" onClick={() => setFullscreenMedia({ items: mediaItems, index: safeIndex })} aria-label="ملء الشاشة">
                          <Maximize size={18} />
                        </button>
                        {total > 1 && (
                          <>
                            <button
                              className="gallery-arrow gallery-arrow-prev"
                              onClick={() => setGalleryIndex(i => Math.max(i - 1, 0))}
                              disabled={safeIndex === 0}
                              aria-label="السابق"
                            >&#8250;</button>
                            <button
                              className="gallery-arrow gallery-arrow-next"
                              onClick={() => setGalleryIndex(i => Math.min(i + 1, total - 1))}
                              disabled={safeIndex === total - 1}
                              aria-label="التالي"
                            >&#8249;</button>
                            <div className="gallery-dots">
                              {(mediaItems.length ? mediaItems : allImages).map((_, idx) => (
                                <button
                                  key={idx}
                                  className={`gallery-dot${idx === safeIndex ? ' active' : ''}`}
                                  onClick={() => setGalleryIndex(idx)}
                                  aria-label={`صورة ${idx + 1}`}
                                />
                              ))}
                            </div>
                            <span className="gallery-counter">{safeIndex + 1} / {total}</span>
                          </>
                        )}
                      </div>
                    );
                  })()}
                  <div className="view-badges">
                    <span className="type-badge" style={getPropertyTypeColor(selectedOffer.propertyType || selectedOffer.type)}>
                      {getPropertyTypeLabel(selectedOffer.propertyType || selectedOffer.type)}
                    </span>
                    {catalog.fields.status && selectedOffer.status && (
                      <span className="status-badge" style={getStatusColor(selectedOffer.status)}>
                        {getStatusLabel(selectedOffer.status)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="view-offer-details">
                  <h3>{selectedOffer.title || 'بدون عنوان'}</h3>

                  {catalog.fields.location && (
                    <div className="view-location">
                      <MapPin size={18} />
                      <span>{selectedOffer.city} - {selectedOffer.district}</span>
                    </div>
                  )}

                  {catalog.fields.price && <div className="view-price">{formatPrice(selectedOffer.price)}</div>}

                  <div className="view-specs-grid">
                    {catalog.fields.area && selectedOffer.area > 0 && (
                      <div className="spec-card">
                        <Maximize size={20} />
                        <div className="spec-info">
                          <span className="spec-label">المساحة</span>
                          <span className="spec-value">{formatArea(selectedOffer.area)}</span>
                        </div>
                      </div>
                    )}
                    {catalog.fields.rooms && selectedOffer.rooms > 0 && (
                      <div className="spec-card">
                        <Bed size={20} />
                        <div className="spec-info">
                          <span className="spec-label">الغرف</span>
                          <span className="spec-value">{selectedOffer.rooms}</span>
                        </div>
                      </div>
                    )}
                    {selectedOffer.bathrooms > 0 && (
                      <div className="spec-card">
                        <Bath size={20} />
                        <div className="spec-info">
                          <span className="spec-label">الحمامات</span>
                          <span className="spec-value">{selectedOffer.bathrooms}</span>
                        </div>
                      </div>
                    )}
                    {selectedOffer.age > 0 && (
                      <div className="spec-card">
                        <Calendar size={20} />
                        <div className="spec-info">
                          <span className="spec-label">عمر العقار</span>
                          <span className="spec-value">{selectedOffer.age} سنة</span>
                        </div>
                      </div>
                    )}
                    {selectedOffer.floors > 0 && (
                      <div className="spec-card">
                        <Building2 size={20} />
                        <div className="spec-info">
                          <span className="spec-label">الأدوار</span>
                          <span className="spec-value">{selectedOffer.floors}</span>
                        </div>
                      </div>
                    )}
                    {selectedOffer.streetWidth > 0 && (
                      <div className="spec-card">
                        <ArrowUpDown size={20} />
                        <div className="spec-info">
                          <span className="spec-label">عرض الشارع</span>
                          <span className="spec-value">{selectedOffer.streetWidth} م</span>
                        </div>
                      </div>
                    )}
                    {selectedOffer.deedType && (
                      <div className="spec-card">
                        <FileText size={20} />
                        <div className="spec-info">
                          <span className="spec-label">نوع الصك</span>
                          <span className="spec-value">{selectedOffer.deedType === 'free' ? 'حر' : 'مرهون'}</span>
                        </div>
                      </div>
                    )}
                    {selectedOffer.direction && (
                      <div className="spec-card">
                        <Compass size={20} />
                        <div className="spec-info">
                          <span className="spec-label">الواجهة</span>
                          <span className="spec-value">{selectedOffer.direction}</span>
                        </div>
                      </div>
                    )}
                    {catalog.fields.offerNature && selectedOffer.offerNature && (
                      <div className="spec-card">
                        <Tag size={20} />
                        <div className="spec-info">
                          <span className="spec-label">صفة العرض</span>
                          <span className="spec-value">{getOfferNatureLabel(selectedOffer.offerNature)}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {catalog.fields.description && selectedOffer.description && (
                    <div className="view-description">
                      <h4>الوصف</h4>
                      <p>{selectedOffer.description}</p>
                    </div>
                  )}

                  {selectedOffer.features && selectedOffer.features.length > 0 && (
                    <div className="view-features">
                      <h4>المميزات</h4>
                      <div className="features-list">
                        {selectedOffer.features.map((feature, idx) => (
                          <span key={idx} className="feature-tag">{feature}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedOffer.externalLinks && selectedOffer.externalLinks.length > 0 && (
                    <div className="view-external-links">
                      <h4>روابط خارجية</h4>
                      <div className="links-list">
                        {selectedOffer.externalLinks.map((link, idx) => (
                          <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="external-link-item">
                            {link.title || link.url}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              {marketer?.whatsapp && selectedOffer && (
                <button
                  className="btn-modal-whatsapp"
                  onClick={() => handleWhatsApp(selectedOffer)}
                >
                  <FaWhatsapp size={18} /> تواصل واتساب
                </button>
              )}
              {selectedOffer && (
                <button
                  className="btn-modal-share"
                  onClick={() => handleShareOffer(null, selectedOffer)}
                >
                  <Share2 size={16} /> مشاركة العرض
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="catalog-footer">
        <p>تم إنشاء هذا الكتالوج بواسطة <strong>{marketer?.name}</strong></p>
      </footer>

      {fullscreenMedia && (
        <div className="catalog-fullscreen-overlay" onClick={() => setFullscreenMedia(null)}>
          <button type="button" className="catalog-fullscreen-close" onClick={() => setFullscreenMedia(null)} aria-label="إغلاق">
            <X size={26} />
          </button>
          <div className="catalog-fullscreen-content" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const items = fullscreenMedia.items || [fullscreenMedia];
              const index = Math.min(fullscreenMedia.index || 0, items.length - 1);
              const active = items[index];
              return (
                <>
                  {active.type === 'video' ? (
                    <video src={active.src} controls autoPlay />
                  ) : (
                    <img src={active.src} alt={active.label || 'صورة العرض'} />
                  )}
                  {items.length > 1 && (
                    <>
                      <button type="button" className="catalog-fullscreen-nav catalog-fullscreen-prev" onClick={() => setFullscreenMedia(prev => ({ ...prev, index: Math.max((prev.index || 0) - 1, 0) }))} disabled={index === 0} aria-label="السابق">&#8250;</button>
                      <button type="button" className="catalog-fullscreen-nav catalog-fullscreen-next" onClick={() => setFullscreenMedia(prev => ({ ...prev, index: Math.min((prev.index || 0) + 1, items.length - 1) }))} disabled={index === items.length - 1} aria-label="التالي">&#8249;</button>
                      <span className="catalog-fullscreen-counter">{index + 1} / {items.length}</span>
                    </>
                  )}
                  <button type="button" className="catalog-fullscreen-toggle" onClick={() => setFullscreenMedia(null)} aria-label="الخروج من ملء الشاشة">
                    <Maximize size={20} />
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      )}
      {showScrollTop && (
        <button
          type="button"
          className="catalog-scroll-top"
          onClick={handleScrollTop}
          aria-label="العودة للأعلى"
          title="العودة للأعلى"
        >
          <ArrowUp size={18} />
        </button>
      )}
    </div>
  );
};

export default PublicCatalogView;
